<template>
  <Transition name="modal-fade">
    <div
      v-if="isModalVisible"
      class="fixed inset-0 flex items-center justify-center p-4 sm:p-6"
      style="background-color: rgba(0, 0, 0, 0.88); z-index: 9999;"
      @click.stop
      @keydown.stop
    >
      <div
        class="modal-card relative w-full max-w-lg rounded-2xl border border-red-500/30 bg-gray-900 p-6 sm:p-8 text-center text-white shadow-2xl overflow-hidden"
      >
        <!-- Acento de resplandor ambiental rojo superior -->
        <div
          class="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-80 h-80 bg-red-600/15 rounded-full blur-3xl"
        ></div>

        <!-- Ícono principal de alerta -->
        <div
          class="relative w-16 h-16 mx-auto rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 mb-4 shadow-lg shadow-red-950/60"
        >
          <ExclamationCircleIcon class="w-9 h-9" aria-hidden="true" />
        </div>

        <!-- Badge de Estado -->
        <div class="mb-3">
          <span
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-300 border border-red-500/30 uppercase tracking-wider"
          >
            <span class="w-2 h-2 rounded-full bg-red-400 animate-pulse"></span>
            Empresa Desactivada
          </span>
        </div>

        <!-- Logo / Nombre de la empresa actual -->
        <div class="flex items-center justify-center space-x-3 mb-3">
          <img
            v-if="activeCompany && activeCompany.logo"
            class="w-10 h-10 rounded-xl object-cover bg-gray-800 border border-gray-700 shadow-sm"
            :src="'https://leadbox.ec/storage/' + activeCompany.logo"
            :alt="(activeCompany.name || 'Empresa') + ' logo'"
            @error="onLogoError"
          />
          <div
            v-else
            class="w-10 h-10 rounded-xl bg-sky-700 flex items-center justify-center font-bold text-white text-xs border border-gray-700 shadow-sm"
          >
            {{ activeCompany && activeCompany.name ? activeCompany.name.substring(0, 2).toUpperCase() : 'CO' }}
          </div>
          <h2 class="text-xl font-bold text-white truncate max-w-xs text-left">
            {{ activeCompany ? activeCompany.name : 'Empresa Actual' }}
          </h2>
        </div>

        <!-- Mensaje descriptivo -->
        <p class="text-xs sm:text-sm text-gray-300 leading-relaxed mb-6">
          {{ displayMessage }}
        </p>

        <!-- Botón Contactar a Soporte -->
        <div class="space-y-3">
          <a
            :href="supportMailto"
            class="w-full inline-flex items-center justify-center px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white font-semibold text-sm shadow-lg shadow-red-950/40 transition-all duration-150 active:scale-[0.99] focus:outline-none"
          >
            <MailIcon class="w-4 h-4 mr-2" aria-hidden="true" />
            <span>Contactar al equipo (negocios@leadbox.ec)</span>
          </a>
        </div>

        <!-- Selector de otra empresa activa (si tiene otras empresas disponibles) -->
        <div v-if="otherActiveCompanies.length > 0" class="mt-6 pt-5 border-t border-gray-800 text-left">
          <div class="flex items-center justify-between mb-2.5">
            <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              O cambie a una empresa activa:
            </span>
            <span class="text-[11px] text-gray-500 font-medium">
              {{ otherActiveCompanies.length }} disponible{{ otherActiveCompanies.length > 1 ? 's' : '' }}
            </span>
          </div>

          <div class="space-y-1.5 max-h-44 overflow-y-auto pr-1 custom-modal-scrollbar">
            <button
              v-for="company in otherActiveCompanies"
              :key="company.uuid || company.id"
              type="button"
              :disabled="isSwitching"
              class="w-full flex items-center justify-between p-2.5 rounded-xl bg-gray-800/80 hover:bg-gray-700/80 border border-gray-700/60 hover:border-sky-500/50 transition-all duration-150 text-left group focus:outline-none"
              @click="selectCompany(company)"
            >
              <div class="flex items-center space-x-3 min-w-0">
                <img
                  v-if="company.logo"
                  class="w-8 h-8 rounded-lg object-cover bg-gray-900 border border-gray-700 shrink-0"
                  :src="'https://leadbox.ec/storage/' + company.logo"
                  :alt="company.name + ' logo'"
                  @error="onLogoError"
                />
                <div
                  v-else
                  class="w-8 h-8 rounded-lg bg-sky-700 flex items-center justify-center font-bold text-white text-xs shrink-0"
                >
                  {{ company.name ? company.name.substring(0, 2).toUpperCase() : 'CO' }}
                </div>
                <span class="text-xs font-medium text-gray-200 group-hover:text-white truncate">
                  {{ company.name }}
                </span>
              </div>
              <span class="text-xs font-semibold text-sky-400 group-hover:text-sky-300 ml-2 shrink-0">
                {{ isSwitching ? 'Cambiando...' : 'Seleccionar →' }}
              </span>
            </button>
          </div>
        </div>

        <!-- Enlace para Cerrar Sesión -->
        <div class="mt-6 pt-4 border-t border-gray-800/80 flex items-center justify-center">
          <button
            type="button"
            class="inline-flex items-center text-xs text-gray-400 hover:text-rose-400 transition-colors py-1.5 px-3 rounded-lg hover:bg-gray-800/60 focus:outline-none"
            @click="closeSession"
          >
            <LogoutIcon class="w-4 h-4 mr-1.5 text-gray-500" aria-hidden="true" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import {
  ref, computed, onMounted, onBeforeUnmount, watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import {
  ExclamationCircleIcon, MailIcon, LogoutIcon,
} from '@heroicons/vue/solid';
import { notify } from '@kyvg/vue3-notification';

const store = useStore();
const route = useRoute();

const suspendedByEvent = ref(false);
const customMessage = ref('');
const isSwitching = ref(false);

const activeCompany = computed(() => store.state.activeCompany);
const companies = computed(() => store.state.companies || []);

const isCompanySuspended = (company) => {
  if (!company) return false;
  return company.status === 'suspended' || Number(company.active) === 0;
};

const otherActiveCompanies = computed(() => companies.value.filter((c) => {
  if (!c) return false;
  const isCurrent = c.uuid === activeCompany.value?.uuid;
  return !isCurrent && !isCompanySuspended(c);
}));

const isModalVisible = computed(() => {
  const userInfo = localStorage.getItem('userInfo');
  const token = localStorage.getItem('accessToken');
  if (!userInfo || !token) {
    return false;
  }

  // No mostrar en la pantalla de login o bienvenida
  if (route.name === 'login' || route.name === 'home' || route.path === '/login' || route.path === '/') {
    return false;
  }

  if (suspendedByEvent.value) {
    return true;
  }

  return isCompanySuspended(activeCompany.value);
});

const displayMessage = computed(() => {
  if (customMessage.value) {
    return customMessage.value;
  }
  const name = activeCompany.value?.name || 'su empresa';
  return `La cuenta de ${name} se encuentra temporalmente inactiva o suspendida. Para reactivar el servicio y recuperar el acceso a sus leads y conversaciones, por favor comuníquese con el equipo a negocios@leadbox.ec.`;
});

const supportMailto = computed(() => {
  const companyName = activeCompany.value?.name || 'Leadbox';
  const subject = encodeURIComponent(`Reactivación de cuenta - ${companyName}`);
  const body = encodeURIComponent(
    `Hola equipo de Leadbox,\n\nSolicito información para la reactivación de la cuenta de la empresa: ${companyName}.\n\nSaludos.`,
  );
  return `mailto:negocios@leadbox.ec?subject=${subject}&body=${body}`;
});

const handleCompanySuspendedEvent = (event) => {
  suspendedByEvent.value = true;
  if (event?.detail?.message) {
    customMessage.value = event.detail.message;
  }
};

const onLogoError = (event) => {
  try {
    // eslint-disable-next-line global-require
    event.target.src = require('@/assets/company_icon.jpg');
  } catch (e) {
    // Fallback silencioso
  }
};

const selectCompany = (company) => {
  if (!company || isSwitching.value) return;
  isSwitching.value = true;

  store.dispatch('setActiveCompany', company)
    .then(() => {
      suspendedByEvent.value = false;
      customMessage.value = '';
      notify({
        type: 'success',
        title: 'Empresa Cambiada',
        text: `Has cambiado a ${company.name}`,
      });
      // Recargar la ventana para reiniciar el contexto de chats/leads con la nueva empresa
      window.location.reload();
    })
    .catch((error) => {
      isSwitching.value = false;
      notify({
        type: 'error',
        title: 'Error',
        text: error?.data?.message || 'Error al cambiar la empresa',
      });
    });
};

const closeSession = () => {
  store.dispatch('clearLeads', {});
  store.dispatch('clearConversations', {});

  localStorage.removeItem('userInfo');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('companyUuid');

  window.location.href = '/login';
};

const toggleBodyScroll = (isOpen) => {
  document.querySelector('body')?.classList.toggle('overflow-hidden', isOpen);
};

watch(isModalVisible, (visible) => {
  toggleBodyScroll(visible);
}, { immediate: true });

onMounted(() => {
  window.addEventListener('company:suspended', handleCompanySuspendedEvent);
});

onBeforeUnmount(() => {
  window.removeEventListener('company:suspended', handleCompanySuspendedEvent);
  toggleBodyScroll(false);
});
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.96);
}

.custom-modal-scrollbar::-webkit-scrollbar {
  width: 5px;
}

.custom-modal-scrollbar::-webkit-scrollbar-track {
  background: #111827;
  border-radius: 4px;
}

.custom-modal-scrollbar::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 4px;
}

.custom-modal-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}
</style>
